/**
 * Batch #14.37 — Configurations des 8 guides web (sauf Immeuble + Autonome).
 *
 * Chaque export = un objet config qui sera passé à GenericWebGuide.
 * Pour ajouter du contenu, modifiez juste les blocks d'une section.
 */
import { FileText, Upload, MessageCircle, Calendar, FileSignature, Receipt, Briefcase, User, BookOpen, Eye, Download as DownloadIcon, CheckCircle2, AlertTriangle, Smartphone, Mail, Phone, CreditCard, ShieldCheck, FileCheck2, Bell, Clock, DollarSign, Settings, Lock } from "lucide-react";

// =============================================================
//  📩 1. DOCUMENTS REÇUS
// =============================================================
export const documentsGuide = {
  slug: "documents",
  label: "Documents reçus",
  icon: FileText,
  accent: "blue",
  gradientFrom: "from-blue-600",
  gradientVia:  "via-blue-700",
  gradientTo:   "to-indigo-700",
  intro: "Consultez et téléchargez en tout temps les documents que votre cabinet partage avec vous : déclarations fiscales (T1), conventions d'honoraires, mandats de représentation, baux signés, etc.",
  sections: [
    {
      id: "introduction", title: "Introduction", icon: BookOpen, accent: "blue",
      blocks: [
        { type: "paragraph", content: "Le module <strong>Documents reçus</strong> est votre boîte aux lettres numérique. Tout ce que votre cabinet vous envoie (déclarations fiscales, conventions d'honoraires, mandats, baux signés, attestations) y est archivé en toute sécurité." },
        { type: "tip", tone: "emerald", icon: CheckCircle2, content: "Tous vos documents restent disponibles <strong>indéfiniment</strong>. Plus besoin de chercher dans vos courriels." },
      ],
    },
    {
      id: "consulter", title: "Consulter un document", icon: Eye, accent: "blue",
      blocks: [
        { type: "screenshot", slug: "liste-documents", alt: "Liste des documents reçus : Bail TAL, Déclaration T1 fédérale, Convention d'honoraires, Mandat de représentation (T1013/MR-69) — avec date, type et statut SIGNÉ ou À SIGNER" },
        { type: "steps", items: [
            { title: "Ouvrez la tuile « Documents »", desc: "Sur le tableau de bord du portail." },
            { title: "Identifiez le type de document", desc: "Chaque ligne affiche le <strong>titre</strong>, la <strong>date d'envoi</strong>, le <strong>type</strong> (T1, Bail, Convention, Mandat) et le <strong>statut</strong> (✓ Signé / ⚠️ À signer)." },
            { title: "Cliquez sur la ligne", desc: "Le document s'affiche directement dans le navigateur (PDF inline), sans téléchargement préalable." },
        ]},
        { type: "tip", tone: "rose", icon: AlertTriangle, content: "Les documents marqués <strong>« À signer »</strong> (badge orange) requièrent votre action. Cliquez dessus pour ouvrir le workflow de signature électronique sécurisé (lien magique unique)." },
      ],
    },
    {
      id: "telecharger", title: "Télécharger / Imprimer", icon: DownloadIcon, accent: "blue",
      blocks: [
        { type: "paragraph", content: "Une fois le document ouvert, cliquez sur <strong>« Télécharger »</strong> pour en garder une copie sur votre appareil, ou sur <strong>« Imprimer »</strong> pour l'envoyer à votre imprimante. Les documents <em>signés électroniquement</em> incluent un <strong>QR code de vérification publique</strong> au bas — n'importe quel tiers (banque, TAL, gouvernement) peut le scanner pour confirmer l'authenticité." },
        { type: "tip", content: "Sur mobile, le téléchargement enregistre le PDF dans vos <em>Téléchargements</em>. Vous pouvez ensuite le partager via Mail, Messages, AirDrop, etc." },
      ],
    },
    {
      id: "notifications", title: "Notifications", icon: Bell, accent: "blue",
      blocks: [
        { type: "paragraph", content: "Vous recevez automatiquement un <strong>courriel</strong> à chaque nouveau document. Si vous avez activé les notifications push (PWA installée), elles apparaîtront aussi sur votre téléphone." },
        { type: "tip", tone: "rose", icon: AlertTriangle, content: "Si vous ne recevez plus les courriels, vérifiez votre dossier <strong>Spam / Indésirables</strong> et ajoutez <em>dgfiscalflow@dgcomptabilite.ca</em> à vos contacts de confiance." },
      ],
    },
  ],
};

// =============================================================
//  📤 2. ENVOYER UN FICHIER
// =============================================================
export const envoyerGuide = {
  slug: "envoyer",
  label: "Envoyer un fichier",
  icon: Upload,
  accent: "emerald",
  gradientFrom: "from-emerald-600",
  gradientVia:  "via-emerald-700",
  gradientTo:   "to-teal-700",
  intro: "Téléversez en toute simplicité vos documents (T4, T5, factures, reçus, etc.) vers votre cabinet, depuis n'importe quel appareil.",
  sections: [
    {
      id: "introduction", title: "Introduction", icon: BookOpen, accent: "emerald",
      blocks: [
        { type: "paragraph", content: "Cette tuile sert à <strong>envoyer rapidement un document</strong> à votre cabinet — sans avoir à passer par votre boîte courriel. Les fichiers arrivent directement dans votre dossier client." },
      ],
    },
    {
      id: "envoyer", title: "Étapes pour envoyer", icon: Upload, accent: "emerald",
      blocks: [
        { type: "screenshot", slug: "envoyer-formulaire", alt: "Formulaire « Envoyer un document » — sélecteur Année fiscale + Catégorie (T4, T5, Reçus médicaux, Don, Frais de scolarité, etc.), zone Glisser-déposer ou bouton « Choisir un fichier », boutons « Annuler » et « Envoyer »" },
        { type: "steps", items: [
            { title: "Choisissez l'année fiscale", desc: "Sélectionnez l'année concernée (ex. 2025) dans le menu déroulant. Important pour que le cabinet classe correctement votre document." },
            { title: "Sélectionnez la catégorie", desc: "Ex. T4, T5, Reçus médicaux, Reçus de dons, Frais de scolarité, Reçu de garderie, Autre. Aide votre cabinet à classer automatiquement." },
            { title: "Glissez le fichier ou cliquez « Choisir un fichier »", desc: "Zone de dépôt visuelle avec icône upload. Vous pouvez aussi cliquer pour ouvrir l'explorateur de fichiers de votre appareil." },
            { title: "Ajoutez une note (optionnel)", desc: "Précisez si nécessaire (ex. <em>« 3<sup>e</sup> T4 reçu de Walmart »</em>, <em>« Reçu de garderie 2025 - 2 enfants »</em>)." },
            { title: "Cliquez « Envoyer »", desc: "Confirmation immédiate à l'écran. Le cabinet est notifié et vous voyez le document apparaître dans votre historique." },
        ]},
      ],
    },
    {
      id: "formats", title: "Formats acceptés", icon: FileCheck2, accent: "emerald",
      blocks: [
        { type: "cards", columns: 3, items: [
            { icon: "📄", title: "PDF", desc: "Format préféré pour tous les documents officiels (T4, factures, contrats).", tone: "emerald" },
            { icon: "🖼️", title: "Photo (JPG / PNG / HEIC)", desc: "Pratique pour les reçus papier — prenez-les en photo avec votre téléphone.", tone: "blue" },
            { icon: "📊", title: "Excel / Word", desc: "Acceptés mais convertis en PDF par le cabinet pour archive.", tone: "violet" },
        ]},
        { type: "tip", tone: "rose", icon: AlertTriangle, content: "Taille maximum : <strong>25 Mo par fichier</strong>. Pour les très gros documents, envoyez en plusieurs envois ou compressez." },
      ],
    },
    {
      id: "mobile", title: "Astuce mobile", icon: Smartphone, accent: "emerald",
      blocks: [
        { type: "paragraph", content: "Sur téléphone, vous pouvez prendre le document <strong>directement en photo</strong> depuis le formulaire. Idéal pour les reçus papier ou factures reçues par la poste." },
      ],
    },
  ],
};

// =============================================================
//  💬 3. CHAT & QUESTIONS
// =============================================================
export const chatGuide = {
  slug: "chat",
  label: "Chat & questions",
  icon: MessageCircle,
  accent: "indigo",
  gradientFrom: "from-indigo-600",
  gradientVia:  "via-indigo-700",
  gradientTo:   "to-purple-700",
  intro: "Communiquez avec votre conseiller en direct, sans courriel ni téléphone. Historique conservé, réponse en moyenne en 3 h ouvrables.",
  sections: [
    {
      id: "introduction", title: "Introduction", icon: BookOpen, accent: "indigo",
      blocks: [
        { type: "paragraph", content: "Le chat est votre <strong>canal privilégié</strong> pour toutes les questions courantes — bien plus rapide que le courriel et plus structuré que le téléphone." },
        { type: "tip", tone: "emerald", icon: CheckCircle2, content: "L'historique complet de vos échanges reste accessible — vous retrouvez n'importe quelle conversation passée." },
      ],
    },
    {
      id: "envoyer-message", title: "Poser une question", icon: MessageCircle, accent: "indigo",
      blocks: [
        { type: "screenshot", slug: "chat-conversation", alt: "Page « Chat clients » — colonne gauche « Mes conversations (0) » + bouton « + Nouvelle question », colonne droite formulaire « Nouvelle question » avec champs Sujet, Catégorie (général/déclaration/document/facture/autre), Priorité (basse/normale/haute/urgente), Message, joindre fichier, boutons « Annuler »/« Envoyer »" },
        { type: "paragraph", content: "Le chat fonctionne par <strong>conversations</strong> (une par sujet). À votre première utilisation, la liste de gauche est vide — cliquez sur <strong>« + Nouvelle question »</strong> pour démarrer." },
        { type: "steps", items: [
            { title: "Rédigez le sujet", desc: "Un titre court et précis. Ex. <em>« Reçu CELI 2024 manquant »</em> ou <em>« Question sur frais médicaux »</em>. Évitez les sujets trop génériques (<em>« Question »</em>, <em>« Aide »</em>)." },
            { title: "Choisissez la catégorie", desc: "<strong>Général</strong> (questions diverses), <strong>Déclaration</strong> (T1, T2, TP), <strong>Document</strong> (concernant un document spécifique), <strong>Facture</strong> (questions sur facturation), <strong>Autre</strong>. Aide votre cabinet à prioriser et router votre demande." },
            { title: "Définissez la priorité", desc: "🟢 <strong>Basse</strong> — pas urgent, réponse possible sous 48 h. 🔵 <strong>Normale</strong> — réponse sous 24 h (défaut). 🟡 <strong>Haute</strong> — important, réponse sous 4 h ouvrables. 🔴 <strong>Urgente</strong> — réservé aux situations critiques (mise en demeure, échéance imminente)." },
            { title: "Rédigez le message", desc: "Soyez <strong>précis</strong> : numéro de dossier si applicable, année fiscale, contexte. Plus vous donnez de détails, moins il y aura d'allers-retours." },
            { title: "Joignez un fichier (optionnel)", desc: "Icône <em>📎 trombone</em> pour ajouter une photo, un PDF ou tout autre document utile à votre question." },
            { title: "Envoyez", desc: "Le cabinet reçoit une notification instantanée. La conversation apparaît dans la colonne de gauche avec un statut <em>« En attente de réponse »</em>." },
        ]},
      ],
    },
    {
      id: "delai", title: "Délai de réponse", icon: Clock, accent: "indigo",
      blocks: [
        { type: "paragraph", content: "Notre équipe répond en moyenne en <strong>3 h ouvrables</strong> (lundi-vendredi 8h-17h). Pour les questions urgentes hors heures, utilisez plutôt le téléphone." },
        { type: "tip", tone: "rose", icon: AlertTriangle, content: "Pour une <strong>urgence fiscale</strong> (avis du gouvernement, mise en demeure, etc.), appelez directement le cabinet." },
      ],
    },
    {
      id: "alternatives", title: "Autres canaux", icon: Phone, accent: "indigo",
      blocks: [
        { type: "cards", columns: 3, items: [
            { icon: MessageCircle, title: "Chat (ici)", desc: "Recommandé · Réponse 3 h", tone: "indigo" },
            { icon: Mail, title: "Courriel", desc: "dgfiscalflow@dgcomptabilite.ca", tone: "blue" },
            { icon: Calendar, title: "Rendez-vous", desc: "Pour discuter en personne ou par vidéo", tone: "violet" },
        ]},
      ],
    },
  ],
};

// =============================================================
//  📅 4. RENDEZ-VOUS
// =============================================================
export const rdvGuide = {
  slug: "rdv",
  label: "Rendez-vous",
  icon: Calendar,
  accent: "violet",
  gradientFrom: "from-violet-600",
  gradientVia:  "via-violet-700",
  gradientTo:   "to-purple-700",
  intro: "Prenez et gérez vos rendez-vous avec votre conseiller directement depuis votre portail. Visio, téléphone ou en personne — vous choisissez.",
  sections: [
    {
      id: "introduction", title: "Introduction", icon: BookOpen, accent: "violet",
      blocks: [
        { type: "paragraph", content: "Le module Rendez-vous vous permet de <strong>demander une rencontre</strong> avec votre conseiller (vidéo, téléphone ou au bureau). Le cabinet valide votre demande et confirme le créneau — vous gardez toujours la visibilité sur le statut." },
      ],
    },
    {
      id: "prendre", title: "Demander un rendez-vous", icon: Calendar, accent: "violet",
      blocks: [
        { type: "screenshot", slug: "rdv-creneaux", alt: "Page « Rendez-vous » — colonne gauche « Demander un rendez-vous » avec champs Date, Heure, Durée (30/45/60/90 min), Type (vidéo Google Meet / téléphone / au bureau), Sujet (déclaration impôts/conseil fiscal/document/dossier complexe/autre), Description ; colonne droite « Mes prochains rendez-vous » avec compteurs et liste des RDV à venir" },
        { type: "paragraph", content: "L'interface est divisée en <strong>2 panneaux</strong> : à gauche le formulaire de demande, à droite vos rendez-vous à venir. Tous les RDV doivent être <strong>confirmés par le cabinet</strong> avant d'être actifs (pas de réservation auto)." },
        { type: "steps", items: [
            { title: "Choisissez la date et l'heure", desc: "Utilisez le sélecteur de date (calendrier) et le menu déroulant des heures. Évitez les heures de pointe (8h-9h et 16h-17h) pour augmenter vos chances que le créneau soit accepté." },
            { title: "Sélectionnez la durée", desc: "<strong>30 min</strong> (question rapide), <strong>45 min</strong> (suivi de dossier), <strong>60 min</strong> (recommandé pour déclarations), ou <strong>90 min</strong> (dossier complexe avec plusieurs sujets)." },
            { title: "Choisissez le type de rencontre", desc: "📹 <strong>Vidéo (Google Meet)</strong> — recommandé, économise les déplacements. 📞 <strong>Téléphone</strong> — pour échanges courts. 🏢 <strong>Au bureau</strong> — si vous devez apporter des documents originaux." },
            { title: "Indiquez le sujet", desc: "Catégorie principale (déclaration d'impôts, conseil fiscal, signature de document, dossier complexe, autre) — aide le cabinet à préparer votre dossier avant la rencontre." },
            { title: "Décrivez votre demande", desc: "<strong>Soyez précis</strong> dans la description : numéro de dossier, contexte, questions précises. Plus vous donnez d'info en amont, plus la rencontre sera productive." },
            { title: "Envoyez la demande", desc: "Le cabinet reçoit une notification. Vous recevez une <strong>confirmation par courriel</strong> dès qu'il accepte (ou propose un autre créneau). Le RDV apparaît alors dans le panneau de droite." },
        ]},
        { type: "tip", tone: "emerald", icon: CheckCircle2, content: "Si le créneau demandé ne convient pas au cabinet, il vous fera une <strong>contre-proposition</strong> (autre date/heure proche). Vous pouvez accepter en 1 clic ou demander un autre créneau, sans repartir de zéro." },
      ],
    },
    {
      id: "modifier", title: "Modifier / annuler", icon: Settings, accent: "violet",
      blocks: [
        { type: "paragraph", content: "Sur la tuile, vos rendez-vous à venir sont listés. Cliquez sur <strong>« Modifier »</strong> ou <strong>« Annuler »</strong> au plus tard <strong>24 h avant</strong>." },
        { type: "tip", tone: "rose", icon: AlertTriangle, content: "Au-delà de ce délai, contactez le cabinet par téléphone ou chat pour reporter." },
      ],
    },
    {
      id: "rappel", title: "Rappels automatiques", icon: Bell, accent: "violet",
      blocks: [
        { type: "paragraph", content: "Vous recevez automatiquement :" },
        { type: "list", items: [
            "Un courriel de confirmation immédiatement",
            "Un rappel <strong>la veille à 18 h</strong>",
            "Un rappel <strong>1 h avant</strong> le rendez-vous",
        ]},
      ],
    },
  ],
};

// =============================================================
//  ✍️ 5. MANDATS / DOCUMENTS À SIGNER
// =============================================================
export const mandatsGuide = {
  slug: "mandats",
  label: "Documents à signer",
  icon: FileSignature,
  accent: "rose",
  gradientFrom: "from-rose-600",
  gradientVia:  "via-rose-700",
  gradientTo:   "to-pink-700",
  intro: "Signez vos mandats, autorisations et autres documents légaux directement depuis votre portail — depuis n'importe quel appareil.",
  sections: [
    {
      id: "introduction", title: "Introduction", icon: BookOpen, accent: "rose",
      blocks: [
        { type: "paragraph", content: "Tous les documents qui requièrent votre signature (mandats T1198, autorisations CRA/RQ, etc.) arrivent dans cette tuile. La signature est <strong>légalement valide</strong> au Canada (Loi sur l'identification électronique)." },
      ],
    },
    {
      id: "signer", title: "Signer un document", icon: FileSignature, accent: "rose",
      blocks: [
        { type: "paragraph", content: "Le portail vous présente d'abord la <strong>liste des documents en attente</strong> de votre signature avec leur nom, leur date de réception et un badge orange « À signer »." },
        { type: "screenshot", slug: "mandat-signature", alt: "Page « Documents à signer » — bandeau jaune « 1 document(s) en attente de votre signature », ligne « Convention honoraires 2026 — Reçu le 30 mai 2026 — À signer — bouton Signer »" },
        { type: "steps", items: [
            { title: "Cliquez sur le bouton « Signer »", desc: "Le document s'ouvre dans une fenêtre modale avec aperçu PDF intégré." },
            { title: "Lisez attentivement le PDF", desc: "Faites défiler le document complet dans la zone d'aperçu. Important pour comprendre vos engagements (durée, tarifs, conditions de résiliation, etc.). Vous pouvez aussi <strong>télécharger une copie</strong> via le bouton ⬇ pour la conserver." },
            { title: "Lisez la note de votre comptable", desc: "Un encadré bleu apparaît sous le PDF avec un message contextuel du cabinet (ex. <em>« Démo : convention d'honoraires à signer »</em>). Précieux pour comprendre le contexte de l'envoi." },
            { title: "Tracez votre signature", desc: "Dans la zone <strong>« Tracez votre signature ci-dessous »</strong>, dessinez votre signature avec votre souris (ordinateur), votre doigt ou un stylet (mobile/tablette). Vous pouvez recommencer autant de fois que nécessaire avec le bouton « Effacer ». " },
            { title: "Valider la signature", desc: "Cliquez sur <strong>« Valider la signature »</strong> en bas à droite. Le PDF final est généré avec votre signature apposée, archivé dans votre coffre-fort, et envoyé par courriel à toutes les parties." },
        ]},
        { type: "screenshot", slug: "mandat-signature-modal", alt: "Modal « Signer : Convention honoraires 2026 » — aperçu PDF intégré (Convention d'honoraires 2026, DG Comptabilité, tarif 250 $/mois), note du comptable en encadré bleu, zone signature « Tracez votre signature ci-dessous », boutons « Effacer », « Annuler » et « Valider la signature »" },
        { type: "tip", tone: "emerald", icon: CheckCircle2, content: "Sur <strong>mobile / tablette</strong>, la signature au doigt est beaucoup plus naturelle qu'à la souris. Si vous trouvez ça difficile à l'ordinateur, ouvrez le portail sur votre téléphone pour signer." },
      ],
    },
    {
      id: "preuve", title: "Preuve légale", icon: ShieldCheck, accent: "rose",
      blocks: [
        { type: "paragraph", content: "Chaque signature est <strong>horodatée</strong> et associée à votre adresse IP. Une copie signée est envoyée par courriel aux 2 parties (vous + cabinet) et archivée dans le portail." },
        { type: "tip", tone: "emerald", icon: CheckCircle2, content: "Vous pouvez retrouver tous vos documents signés dans la tuile <strong>« Documents reçus »</strong> à tout moment." },
      ],
    },
  ],
};

// =============================================================
//  💰 6. FACTURES CABINET
// =============================================================
export const facturesGuide = {
  slug: "factures",
  label: "Factures cabinet",
  icon: Receipt,
  accent: "amber",
  gradientFrom: "from-amber-600",
  gradientVia:  "via-orange-600",
  gradientTo:   "to-red-600",
  intro: "Consultez et payez vos factures de services comptables directement en ligne — par carte, virement Interac ou chèque.",
  sections: [
    {
      id: "introduction", title: "Introduction", icon: BookOpen, accent: "amber",
      blocks: [
        { type: "paragraph", content: "Cette tuile regroupe <strong>toutes vos factures émises par le cabinet</strong> : impôts personnels annuels, déclarations corporatives, services à la carte, etc." },
      ],
    },
    {
      id: "consulter", title: "Consulter et payer une facture", icon: Eye, accent: "amber",
      blocks: [
        { type: "screenshot", slug: "facture-detail", alt: "Page « Factures » — bandeau jaune « 1 180,29 $ en attente de paiement sur 6 facture(s) », liste de factures avec n°, statut, dates émise/échéance, montant, solde, boutons « Télécharger le PDF » et « Payer par carte »" },
        { type: "paragraph", content: "Le bandeau jaune en haut récapitule le <strong>total en attente de paiement</strong> et le <strong>nombre de factures</strong>. Chaque facture affiche :" },
        { type: "list", items: [
            "<strong>Numéro</strong> (ex. <em>#DEMO-2025-005</em>) et <strong>statut</strong> (sent, paid, overdue)",
            "<strong>Année fiscale</strong> concernée",
            "<strong>Dates</strong> d'émission et d'échéance, ainsi que le <strong>nombre de lignes</strong> détaillées",
            "<strong>Montant total</strong> et <strong>solde restant</strong> à payer (en orange si > 0)",
            "Une <strong>note</strong> du cabinet en italique pour contexte",
        ]},
        { type: "paragraph", content: "Cliquez sur <strong>« Télécharger le PDF »</strong> pour conserver la facture (idéal pour archivage personnel ou présentation à un employeur si déductible)." },
      ],
    },
    {
      id: "payer", title: "Modes de paiement", icon: CreditCard, accent: "amber",
      blocks: [
        { type: "cards", columns: 3, items: [
            { icon: "💳", title: "Carte (Stripe)", desc: "Visa, MasterCard, AMEX. Paiement immédiat sécurisé. Reçu automatique.", tone: "emerald" },
            { icon: "🏦", title: "Virement Interac", desc: "À l'adresse <em>dgfiscalflow@dgcomptabilite.ca</em>. Le portail vous donne la référence.", tone: "blue" },
            { icon: "📝", title: "Chèque", desc: "À l'ordre de <em>Dominik Gougeon</em>. Adresse au bas de la facture.", tone: "amber" },
        ]},
      ],
    },
    {
      id: "recu", title: "Reçus de paiement", icon: FileCheck2, accent: "amber",
      blocks: [
        { type: "paragraph", content: "Chaque paiement génère automatiquement un <strong>reçu PDF</strong> archivé dans la facture. Idéal pour vos déclarations fiscales si la facture est déductible." },
      ],
    },
  ],
};

// =============================================================
//  🏡 7. BUREAU À DOMICILE (T777)
// =============================================================
export const t777Guide = {
  slug: "t777",
  label: "Bureau à domicile",
  icon: Briefcase,
  accent: "amber",
  gradientFrom: "from-amber-600",
  gradientVia:  "via-yellow-600",
  gradientTo:   "to-orange-600",
  intro: "Déclarez vos dépenses de bureau à domicile (formulaire T777) pour optimiser vos déductions fiscales si vous travaillez de la maison.",
  sections: [
    {
      id: "introduction", title: "Introduction", icon: BookOpen, accent: "amber",
      blocks: [
        { type: "paragraph", content: "Le <strong>T777</strong> est une déclaration fédérale qui permet de déduire une partie de vos dépenses domestiques (loyer, électricité, chauffage, internet, etc.) si vous travaillez de la maison <strong>plus de 50 % du temps</strong> ou pour répondre à une exigence de votre employeur." },
        { type: "tip", tone: "rose", icon: AlertTriangle, content: "Le calcul est complexe. Le portail simplifie la collecte des données — c'est votre cabinet qui fait l'application correcte au formulaire CRA/RQ." },
      ],
    },
    {
      id: "donnees", title: "Données à fournir", icon: FileText, accent: "amber",
      blocks: [
        { type: "paragraph", content: "Pour chaque année fiscale, vous devez nous transmettre :" },
        { type: "list", items: [
            "<strong>Superficie du bureau</strong> en pieds carrés (uniquement le bureau, pas toute la maison)",
            "<strong>Superficie totale</strong> de votre logement en pieds carrés",
            "<strong>Loyer annuel</strong> (si locataire) ou <strong>intérêts hypothécaires</strong> (si propriétaire)",
            "<strong>Coûts annuels</strong> : électricité, chauffage, internet, assurance habitation",
            "<strong>Nombre d'heures travaillées</strong> à la maison (semaine / mois / année)",
        ]},
      ],
    },
    {
      id: "remplir", title: "Remplir le formulaire", icon: FileSignature, accent: "amber",
      blocks: [
        { type: "screenshot", slug: "t777-formulaire", alt: "Formulaire « Bureau à domicile et dépenses d'emploi » — sélecteur année fiscale, sections Heures de travail (heures/semaine + nombre de semaines), Superficies (bureau ft² / logement ft²) avec ratio % automatique, Dépenses annuelles (loyer/intérêts hypothèque, électricité, chauffage, internet, assurance, etc.), zone Pièces justificatives et bouton Soumettre" },
        { type: "steps", items: [
            { title: "Sélectionnez l'année fiscale", desc: "Menu déroulant en haut (ex. <em>2025</em>). Une déclaration par année." },
            { title: "Section « Heures de travail »", desc: "Indiquez le nombre <strong>d'heures travaillées par semaine</strong> à la maison + le <strong>nombre de semaines</strong> dans l'année. Le total annuel s'affiche automatiquement." },
            { title: "Section « Superficies »", desc: "Saisissez la <strong>superficie du bureau</strong> (en pi²) et la <strong>superficie totale</strong> du logement (en pi²). Le <strong>ratio %</strong> est calculé en direct — il déterminera la portion de vos dépenses déductibles." },
            { title: "Section « Dépenses annuelles »", desc: "Entrez les <strong>totaux annuels</strong> par poste : loyer ou intérêts hypothécaires, électricité, chauffage, internet, assurance habitation, frais de copropriété, etc. <em>Ne calculez pas la portion bureau vous-même — le système le fera automatiquement.</em>" },
            { title: "Joindre les pièces justificatives (recommandé)", desc: "Téléversez factures, baux et relevés annuels (PDF ou photos). Permet à votre cabinet de valider rapidement sans demande de pièces supplémentaires." },
            { title: "Soumettez", desc: "Cliquez sur <strong>« Soumettre »</strong>. Le formulaire passe en statut <em>« À traiter »</em> côté cabinet et apparaîtra dans votre déclaration T1 annuelle." },
        ]},
        { type: "tip", tone: "emerald", icon: CheckCircle2, content: "Le calcul détaillé est <strong>fait par votre cabinet</strong>. Votre rôle est uniquement de collecter les bonnes données — pas de faire la conversion CRA / RQ vous-même." },
      ],
    },
    {
      id: "details", title: "Coût et délais", icon: DollarSign, accent: "amber",
      blocks: [
        { type: "paragraph", content: "L'inclusion du T777 dans votre déclaration peut être incluse dans votre forfait annuel ou facturée à part selon la complexité. <strong>Contactez DG Comptabilité</strong> pour connaître le tarif." },
      ],
    },
  ],
};

// =============================================================
//  👤 8. MON PROFIL
// =============================================================
export const infoGuide = {
  slug: "info",
  label: "Mon profil",
  icon: User,
  accent: "cyan",
  gradientFrom: "from-cyan-600",
  gradientVia:  "via-teal-700",
  gradientTo:   "to-emerald-700",
  intro: "Gérez vos informations personnelles, vos accès et la sécurité de votre compte portail.",
  sections: [
    {
      id: "introduction", title: "Introduction", icon: BookOpen, accent: "cyan",
      blocks: [
        { type: "paragraph", content: "Votre profil regroupe toutes les informations qui vous identifient (nom, adresse, courriel) et qui permettent à votre cabinet de vous joindre rapidement." },
      ],
    },
    {
      id: "infos-perso", title: "Informations personnelles", icon: User, accent: "cyan",
      blocks: [
        { type: "screenshot", slug: "profil-formulaire", alt: "Formulaire « Mes informations » — sections Identité (Prénom, Nom, Date de naissance), Coordonnées (Courriel, Téléphone), Adresse postale (Rue, Ville, Province, Code postal), avec bouton Enregistrer" },
        { type: "paragraph", content: "Le formulaire est divisé en <strong>3 sections claires</strong> :" },
        { type: "list", items: [
            "<strong>Identité</strong> — Prénom, Nom, Date de naissance (vérifiables sur votre carte de l'assurance maladie)",
            "<strong>Coordonnées</strong> — Courriel et téléphone (utilisés pour les notifications et urgences)",
            "<strong>Adresse postale</strong> — Rue, Ville, Province, Code postal (utilisée pour les déclarations T1)",
        ]},
        { type: "paragraph", content: "Modifiez les champs nécessaires, puis cliquez sur <strong>« Enregistrer »</strong> en bas du formulaire. Le cabinet est automatiquement notifié des changements importants (changement d'adresse, de courriel)." },
        { type: "tip", tone: "rose", icon: AlertTriangle, content: "Le <strong>NAS (Numéro d'Assurance Sociale)</strong> n'est pas modifiable depuis ce formulaire pour des raisons de sécurité. S'il doit être corrigé, contactez directement votre cabinet via le <em>Chat</em> avec votre pièce d'identité scannée." },
      ],
    },
    {
      id: "securite", title: "Sécurité du compte", icon: Lock, accent: "cyan",
      blocks: [
        { type: "paragraph", content: "Vous pouvez à tout moment :" },
        { type: "list", items: [
            "<strong>Changer votre mot de passe</strong> (bouton dans la section Sécurité)",
            "<strong>Vous déconnecter</strong> de tous les appareils en cas de doute",
            "<strong>Activer les notifications</strong> par courriel ou push (si PWA installée)",
        ]},
        { type: "tip", tone: "emerald", icon: CheckCircle2, content: "Votre mot de passe est chiffré (bcrypt) et nous ne pouvons jamais le voir. En cas d'oubli, utilisez « Mot de passe oublié » sur la page de connexion." },
      ],
    },
    {
      id: "pwa", title: "Installer comme application", icon: Smartphone, accent: "cyan",
      blocks: [
        { type: "paragraph", content: "Pour un accès rapide depuis votre téléphone ou ordinateur :" },
        { type: "cards", columns: 3, items: [
            { icon: "🍎", title: "iPhone (Safari)", desc: "Bouton Partager → « Sur l'écran d'accueil »", tone: "blue" },
            { icon: "🤖", title: "Android (Chrome)", desc: "Menu (⋮) → « Ajouter à l'écran d'accueil »", tone: "emerald" },
            { icon: "💻", title: "Ordinateur (Chrome)", desc: "Icône d'installation dans la barre d'adresse", tone: "violet" },
        ]},
      ],
    },
  ],
};
